import {
  Modal,
  PrimaryButton,
  SecondaryButton,
  Typography,
  SearchField,
  MultiSelect,
  PriceBandIcon,
} from '@weareopenr/component-library';
import { useForm } from 'react-hook-form';
import { useMemo } from 'react';
import { IGetVenuesResponseData } from 'src/api/getVenues/types';
import SelectableListWithControls from '../SelectableListWithControls/SelectableListWithControls';
import styles from './VenueAllocationModal.module.scss';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { INestedVenueCategory, IVenueCategory } from 'src/api/getVenueCategories';
import {
  venueCategoriesToSelectOptions,
  venuesInSelectedCategories,
} from 'src/utils/venueCategories/mappedVenueCategories';

/**
 * Props for VenueAllocationForm component.
 *
 * This component can be used in both modal and non-modal contexts (e.g., wizards, multi-step forms).
 *
 * @remarks
 * - `entityId`: While originally designed for category IDs, this can represent any context identifier
 *   (e.g., "price-scheme", category ID, etc.). The value is passed back in the submit payload as `categoryId`.
 *
 * - `onModalClose`: Despite the name, this handler is called when the user cancels/closes the form,
 *   regardless of whether it's used in a modal or not. In wizard contexts, this typically navigates
 *   to the previous step.
 *
 * - `categoryId` in submit payload: The submit callback returns `categoryId` as the field name for
 *   the `entityId` value. This maintains backward compatibility with existing code that expects
 *   `categoryId`, even when used in non-category contexts.
 *
 * - `title` and `subtitle`: When provided, these override the default "Venue Allocation for {entityName}"
 *   header, allowing customization for different use cases (e.g., "Price Scheme Destination").
 *
 * - `cancelButtonLabel` and `submitButtonLabel`: Customize button text for different contexts
 *   (e.g., "Back"/"Continue" for wizards vs "Cancel"/"Save" for modals).
 */
interface IVenueCategoryAllocationForm {
  /** Identifier for the context (category ID, price scheme ID, etc.). Returned as `categoryId` in submit payload. */
  entityId: string;
  /** Display name for the entity being allocated venues */
  entityName: string;
  /** List of available venues to allocate */
  venues: IGetVenuesResponseData[];
  /** Currently selected venue IDs */
  selectedVenueIds?: string[];
  /** Handler called when user cancels/closes the form (works in both modal and non-modal contexts) */
  onModalClose: () => void;
  /** Handler called on form submission with selected venue IDs and the entityId as categoryId */
  onSubmit: (data: { categoryId: string; venueIds: string[] }) => void;
  /** Whether the form is in a loading state */
  isLoading?: boolean;
  /** Flat list of venue categories for filtering */
  venueCategories: IVenueCategory[];
  /** Nested venue categories for hierarchical display in filters */
  nestedVenueCategories: INestedVenueCategory[];
  /** Optional custom title to override default "Venue Allocation for {entityName}" */
  title?: string;
  /** Optional subtitle to provide additional context */
  subtitle?: string;
  /** Label for the cancel/back button (defaults to "Cancel") */
  cancelButtonLabel?: string;
  /** Label for the submit/continue button (defaults to "Save") */
  submitButtonLabel?: string;
  /** Whether to disable submit button when no venues are selected (defaults to false) */
  disableSubmitWhenEmpty?: boolean;
}

interface IVenueAllocationModal extends IVenueCategoryAllocationForm {
  isModalOpen: boolean;
}

interface IFormValues {
  filteredText: string;
  filteredCategoryIds: string[];
  venueIds: string[];
}

/**
 * VenueAllocationForm - A reusable form for selecting and allocating venues.
 *
 * This component provides venue selection functionality with category-based filtering.
 * It can be used standalone (e.g., in wizards) or wrapped in a modal.
 *
 * Key features:
 * - Search venues by name
 * - Filter venues by category
 * - Select/deselect individual or all venues
 * - Displays selected venue count
 *
 * @example
 * // In a modal context (venue categories)
 * <VenueAllocationForm
 *   entityId={categoryId}
 *   entityName="Food Category"
 *   onModalClose={handleClose}
 *   {...otherProps}
 * />
 *
 * @example
 * // In a wizard/multi-step form context
 * <VenueAllocationForm
 *   entityId="price-scheme"
 *   entityName="Price Scheme"
 *   title="Price Scheme Destination"
 *   subtitle="Choose venues for pricing data"
 *   onModalClose={handleBackToPreviousStep}
 *   cancelButtonLabel="Back"
 *   submitButtonLabel="Continue"
 *   disableSubmitWhenEmpty={true}
 *   {...otherProps}
 * />
 */
const VenueAllocationForm = ({
  entityId,
  entityName,
  venues,
  selectedVenueIds = [],
  onModalClose,
  onSubmit: handleFormSubmit,
  isLoading = false,
  venueCategories,
  nestedVenueCategories,
  title,
  subtitle,
  cancelButtonLabel = 'Cancel',
  submitButtonLabel = 'Save',
  disableSubmitWhenEmpty = false,
}: IVenueCategoryAllocationForm) => {
  const formFields = useForm<IFormValues>({
    defaultValues: {
      filteredText: '',
      filteredCategoryIds: [],
      venueIds: selectedVenueIds,
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
  } = formFields;

  const [venueIds, filteredText, filteredCategoryIds] = watch(['venueIds', 'filteredText', 'filteredCategoryIds']);
  const searchQuery = useDebounce(filteredText);

  const venueOptions = useMemo(
    () =>
      venues.map((venue) => ({
        id: venue.venue_id,
        label: venue.name,
      })),
    [venues],
  );

  const onSubmit = async (data: IFormValues) => {
    handleFormSubmit({
      categoryId: entityId,
      venueIds: data.venueIds,
    });
  };

  const handleVenueChange = (selectedVenues: string[]) => {
    setValue('venueIds', selectedVenues, { shouldDirty: true });
  };

  const mappedVenueCategories = useMemo(
    () => venueCategoriesToSelectOptions(nestedVenueCategories),
    [nestedVenueCategories],
  );

  const venuesInSelectedCats = venuesInSelectedCategories(filteredCategoryIds, venueCategories);

  const filteredOptions = useMemo(
    () =>
      venueOptions.filter(({ label, id }) => {
        // Check if venue name matches search query
        const matchesSearch = searchQuery ? label.toLowerCase().includes(searchQuery.toLowerCase()) : true;

        // If no categories selected, show all venues that match search
        if (filteredCategoryIds.length === 0) {
          return matchesSearch;
        }

        // If categories are selected, show only venues in those categories that also match search
        return venuesInSelectedCats.includes(id) && matchesSearch;
      }),
    [venueOptions, searchQuery, filteredCategoryIds, venuesInSelectedCats],
  );

  return (
    <form className={styles['venue-allocation-form']} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles['header-section']}>
        <Typography size="xxlarge" weight="semi-bold">
          {title ? title : `Venue Allocation for ${entityName}`}
        </Typography>
        {subtitle && <Typography>{subtitle}</Typography>}
      </div>
      <div className={styles['venues-section']}>
        <SelectableListWithControls options={filteredOptions} selectedOptions={venueIds} onChange={handleVenueChange}>
          <SearchField
            isDisabled={isLoading}
            placeholder="Search"
            className={styles['filter-searchfield']}
            onChange={({ target }) => setValue('filteredText', target.value)}
          />
          <MultiSelect
            Icon={PriceBandIcon}
            name="venue-categories"
            label="Categories"
            className={styles['filter-multiselect']}
            selected={filteredCategoryIds}
            options={mappedVenueCategories}
            onChange={(ids) => setValue('filteredCategoryIds', ids)}
          />
        </SelectableListWithControls>
      </div>
      <div className={styles['buttons-container']}>
        <ButtonContainer
          left={
            <SecondaryButton size="full-width" buttonStyle="outline" onClick={onModalClose}>
              {cancelButtonLabel}
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              loading={isSubmitting || isLoading}
              size="full-width"
              type="submit"
              disabled={isLoading || isSubmitting || (disableSubmitWhenEmpty && venueIds.length === 0)}
            >
              {submitButtonLabel}
            </PrimaryButton>
          }
        />
      </div>
    </form>
  );
};

const VenueAllocationModal = ({
  isModalOpen,
  onModalClose,
  entityId,
  entityName,
  venues,
  selectedVenueIds,
  onSubmit,
  isLoading,
  venueCategories,
  nestedVenueCategories,
  title,
  subtitle,
  cancelButtonLabel,
  submitButtonLabel,
  disableSubmitWhenEmpty,
}: IVenueAllocationModal) => {
  return (
    <Modal open={isModalOpen} closeModal={onModalClose} contentPosition="center">
      <VenueAllocationForm
        venueCategories={venueCategories}
        nestedVenueCategories={nestedVenueCategories}
        entityId={entityId}
        entityName={entityName}
        venues={venues}
        selectedVenueIds={selectedVenueIds}
        onModalClose={onModalClose}
        onSubmit={onSubmit}
        isLoading={isLoading}
        title={title}
        subtitle={subtitle}
        cancelButtonLabel={cancelButtonLabel}
        submitButtonLabel={submitButtonLabel}
        disableSubmitWhenEmpty={disableSubmitWhenEmpty}
      />
    </Modal>
  );
};

export { VenueAllocationForm };
export default VenueAllocationModal;
